import { createContext, useContext, useEffect, useState } from "react";
import { login as apiLogin } from "@/api/auth";
import {
  setAuth,
  clearAuth,
  getStoredUser,
  getStoredToken,
} from "@/api/client";

const AppContext = createContext(null);

const STORAGE_KEYS = {
  theme: "warranty.theme",
  fontSize: "warranty.fontSize",
  formLayout: "warranty.formLayout",
  setupCompleted: "warranty.setupCompleted",
};

const readStored = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : raw;
  } catch {
    return fallback;
  }
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = getStoredUser();
    if (!stored || !getStoredToken()) return null;
    return stored;
  });

  const [theme, setThemeState] = useState(() =>
    readStored(STORAGE_KEYS.theme, "light") === "dark" ? "dark" : "light"
  );

  const [fontSize, setFontSizeState] = useState(() =>
    readStored(STORAGE_KEYS.fontSize, "normal") === "large" ? "large" : "normal"
  );

  const [formLayout, setFormLayoutState] = useState(() =>
    readStored(STORAGE_KEYS.formLayout, "single") === "slides" ? "slides" : "single"
  );

  const [setupCompleted, setSetupCompletedState] = useState(
    () => readStored(STORAGE_KEYS.setupCompleted, "false") === "true"
  );

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem(STORAGE_KEYS.theme, theme);
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    if (fontSize === "large") root.classList.add("large-text");
    else root.classList.remove("large-text");
    localStorage.setItem(STORAGE_KEYS.fontSize, fontSize);
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.formLayout, formLayout);
  }, [formLayout]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.setupCompleted, String(setupCompleted));
  }, [setupCompleted]);

  const login = async (username, password) => {
    const data = await apiLogin(username, password);
    const token = data?.token;
    const userPayload = data?.user || {};

    if (!token) {
      throw new Error("Respuesta de login inválida");
    }

    if (userPayload?.profile && userPayload.profile !== "Engineer" && userPayload.profile !== "Admin") {
      throw new Error("Esta app es solo para ingenieros");
    }

    const displayName = userPayload.username || username;

    const normalized = {
      userUid: userPayload.userUid || userPayload.uid,
      email: userPayload.email || null,
      username: userPayload.username || username,
      profile: userPayload.profile || "Engineer",
      organizationName: userPayload.organizationName || null,
      name: displayName,
      displayName,
    };

    setAuth(token, normalized);
    setUser(normalized);
    return normalized;
  };

  const logout = () => {
    clearAuth();
    setUser(null);
  };

  const setTheme = (t) => setThemeState(t === "dark" ? "dark" : "light");
  const toggleTheme = () => setThemeState((t) => (t === "dark" ? "light" : "dark"));

  const setFontSize = (s) => setFontSizeState(s === "large" ? "large" : "normal");
  const setFormLayout = (l) => setFormLayoutState(l === "slides" ? "slides" : "single");
  const completeSetup = () => setSetupCompletedState(true);
  const resetSetup = () => setSetupCompletedState(false);

  return (
    <AppContext.Provider
      value={{
        user,
        login,
        logout,
        theme,
        setTheme,
        toggleTheme,
        fontSize,
        setFontSize,
        formLayout,
        setFormLayout,
        setupCompleted,
        completeSetup,
        resetSetup,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}
